/**
 * The agency's own calls for this phase: its package and credits, the onboarding wizard
 * and the client import wizard. Every call goes through `apiRequest`, so auth, refresh and
 * error mapping match the rest of the app.
 */
import { apiRequest, ApiError, type Paginated } from "@/lib/api";
import { saveFile } from "@/lib/blob";
import { readAccessToken } from "@/lib/tokens";

// ---------------------------------------------------------------- billing

export interface BillingSummary {
  tariff: {
    name: string;
    monthly_price: string;
    max_members: number;
    monthly_ai_credits: number;
    extra_credit_price: string;
  } | null;
  period_start: string | null;
  period_end: string | null;
  wallet: { package: number; purchased: number; total: number; expiring_soon: number };
  balance: string;
  daily_spend: string;
  days_left: number | null;
  ledger: { at: string; kind: string; kind_display: string; amount: string; note: string }[];
  usage: { at: string; credits: number; source_kind: string; note: string }[];
}

export interface CreditRequest {
  id: number;
  credits: number;
  amount: string;
  status: "pending" | "confirmed" | "rejected";
  status_display: string;
  note: string;
  created_at: string;
  decided_at: string | null;
  decision_reason: string;
}

// ---------------------------------------------------------------- onboarding

export type StepState = "pending" | "done" | "skipped";

export interface OnboardingStep {
  step: string;
  title: string;
  requirement: string;
  state: StepState;
}

export interface OnboardingPayload {
  steps: OnboardingStep[];
  settled: number;
  total: number;
  is_complete: boolean;
}

// ---------------------------------------------------------------- imports

export interface ImportField {
  name: string;
  label: string;
  required: boolean;
}

export type ImportStatusValue = "draft" | "imported" | "reverted";

export interface ImportBatch {
  id: number;
  kind: string;
  status: ImportStatusValue;
  status_display: string;
  original_filename: string;
  column_mapping: Record<string, string>;
  total_rows: number;
  imported_rows: number;
  failed_rows: number;
  revert_deadline: string | null;
  imported_at?: string | null;
  created_at?: string;
  created_by_name?: string;
  fields: ImportField[];
  headers?: string[];
  missing_required?: string[];
  preview?: { row_number: number; values: Record<string, string>; error: string; is_duplicate: boolean }[];
  will_import?: number;
  will_skip?: number;
}

export const agencyApi = {
  billingSummary: (): Promise<BillingSummary> => apiRequest("/api/billing/summary/"),

  /** How long the AI keeps running, for the frame's banner. `credits` is null unless the role reads billing. */
  billingStanding: (): Promise<{ days_left: number | null; credits: number | null }> => apiRequest("/api/billing/standing/"),

  creditRequests: (page: number): Promise<Paginated<CreditRequest>> =>
    apiRequest("/api/billing/credit-requests/", { query: { page } }),

  requestCredits: (credits: number, note: string): Promise<CreditRequest> =>
    apiRequest("/api/billing/credit-requests/", { method: "POST", body: { credits, note } }),

  onboarding: (): Promise<OnboardingPayload> => apiRequest("/api/onboarding/"),

  setOnboardingStep: (step: string, state: StepState): Promise<OnboardingPayload> =>
    apiRequest(`/api/onboarding/steps/${step}/`, { method: "PATCH", body: { state } }),

  imports: (): Promise<Paginated<ImportBatch>> => apiRequest("/api/imports/"),

  importBatch: (id: number): Promise<ImportBatch> => apiRequest(`/api/imports/${id}/`),

  uploadImport: (file: File): Promise<ImportBatch> => {
    const body = new FormData();
    body.append("kind", "clients");
    body.append("file", file);
    return apiRequest("/api/imports/", { method: "POST", body });
  },


  setImportMapping: (id: number, mapping: Record<string, string>): Promise<ImportBatch> =>
    apiRequest(`/api/imports/${id}/mapping/`, { method: "PATCH", body: { column_mapping: mapping } }),

  commitImport: (id: number): Promise<ImportBatch> =>
    apiRequest(`/api/imports/${id}/commit/`, { method: "POST" }),

  rollbackImport: (id: number): Promise<ImportBatch> =>
    apiRequest(`/api/imports/${id}/rollback/`, { method: "POST" }),
};

/**
 * Fetches a file the API serves and saves it. The link cannot be a plain anchor, because
 * the access token travels in a header, not a cookie.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const base = import.meta.env.VITE_API_BASE_URL.replace(/\/$/, "");
  const token = readAccessToken();
  const response = await fetch(`${base}${path}`, {
    credentials: "include",
    headers: token === null ? {} : { authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new ApiError(response.status, null, `Yuklab olinmadi (${response.status}).`);
  }
  // Saved, never opened: saveFile stores the bytes as application/octet-stream (lib/blob.ts).
  saveFile(await response.blob(), filename);
}
